// =============================================================================
// Lift plan maths — T-436
// =============================================================================
// Kept out of the components so the numbers are testable and there is exactly
// ONE place capacity is decided. Benchmarked line-by-line against three real
// studies James supplied (GBP Walcha, Borger Park Ridge, Ramsamy Andergrove).
//
// THE RULE THAT MATTERS: we never invent a capacity. Off the charted envelope
// returns null and the UI says "off chart".
//
// T-436b (external review, 06/08): we no longer INTERPOLATE between charted
// radii either. Capacity curves are not linear, so a straight-line read between
// two chart rows can OVERSTATE what the machine will lift. Where a calculated
// radius falls between charted radii we now take the next GREATER charted
// radius — the less favourable of the two. Every figure this produces is equal
// to or more conservative than before. Extrapolating past the last row remains
// forbidden.
// =============================================================================

export type ChartPoint = { boom_length_m: number; radius_m: number; variant: string; capacity_kg: number };

export type RiggingItem = {
  item: string; description?: string; rating_t?: number | null;
  length_m?: number | null; qty: number; unit_kg: number;
};

export type Stage = {
  name: string;
  // NOTE: the crane lives on the PLAN, not the stage — one crane per lift plan
  // (James, 01/08). Kept optional here only so plans saved before that change
  // still load; the plan-level config always wins.
  crane_config_id?: string | null;
  piece_desc: string;
  piece_length_m: number;    // how tall this piece is — the height of THIS lift
  built_height_m: number;    // what's already standing when this lift starts
  piece_weight_kg: number;
  other_allowance_kg: number;      // handrails, ice, trapped water
  hook_kg: number;                 // hook block
  rope_kg: number;                 // wire rope in the fall
  rigging: RiggingItem[];
  boom_length_m: number;
  variant: string;                 // Franna articulation etc; "" otherwise
  pick_radius_m: number;
  place_radius_m: number;
  // The pick point belongs to the LIFT, not the drawing. It used to live on the
  // canvas, so dragging it while setting up lift 2 silently rewrote lift 1's
  // radius and its capacity with it (James, 01/08). Undefined = fall back to the
  // canvas position, so plans drawn before this still open unchanged.
  pick_x_m?: number;
  pick_y_m?: number;
  // T-436c — which pole section on the mud map this lift actually picks. When
  // set, the pick position (and therefore the radius, and therefore the
  // capacity) follows THAT module. Before this, every lift inherited one shared
  // pick point out at the far end of the laydown, so a short pick was costed as
  // a long one (James, 06/08). Null/undefined = a hand-placed pick point.
  pick_item_id?: string | null;
  dual_lift: boolean;              // 20% dual-lift factor (Borger convention)
  notes?: string;
};

export type StageResult = {
  riggingKg: number;
  fixedKg: number;                 // hook + rope + rigging
  pieceKg: number;                 // piece + allowances
  grossKg: number;                 // what the crane actually feels
  governingRadiusM: number;        // the worse of pick and place
  chartKg: number | null;          // null = off chart
  chartRadiusM: number | null;     // the charted radius the capacity was READ AT
                                   // (>= governing radius, since we round out)
  pctChart: number | null;
  pctWinch: number | null;
  hookHeightM: number | null;
  band: "ok" | "caution" | "over" | "unknown";
  warnings: string[];
};

export const DUAL_LIFT_FACTOR = 0.20;   // Borger: 20% of piece + fixed weight

/** Sum a rigging bill. Qty x unit weight, nothing clever. */
export function riggingWeightKg(items: RiggingItem[] = []): number {
  return items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_kg) || 0), 0);
}

/**
 * Capacity at a radius for a given boom length, from the chart we hold.
 * Does NOT interpolate: where the radius falls between two charted radii the
 * next GREATER charted radius is used, because capacity falls as radius grows,
 * so that is the conservative read. Returns null outside the charted range —
 * the caller must surface that as "off chart", never guess.
 */
export function chartCapacityKg(
  points: ChartPoint[], boomM: number, radiusM: number, variant = "",
): number | null {
  const col = points
    .filter(p => Number(p.boom_length_m) === Number(boomM) && (p.variant ?? "") === (variant ?? ""))
    .sort((a, b) => a.radius_m - b.radius_m);
  if (col.length === 0) return null;

  let lo: ChartPoint | null = null;
  let hi: ChartPoint | null = null;
  for (const p of col) {
    if (p.radius_m <= radiusM) lo = p;
    if (p.radius_m >= radiusM && hi === null) hi = p;
  }
  if (!lo || !hi) return null;                       // beyond the charted envelope
  if (lo.radius_m === hi.radius_m) return lo.capacity_kg;   // exactly on a chart row
  // Between two rows: take the greater radius (lower capacity). Never a
  // straight-line read between them — see the note at the top of this file.
  return hi.capacity_kg;
}

/** The charted radius a capacity read lands on — the next row at or beyond the
 *  calculated radius. Null when off chart. Mirrors chartCapacityKg exactly. */
export function chartRadiusUsedM(
  points: ChartPoint[], boomM: number, radiusM: number, variant = "",
): number | null {
  const col = points
    .filter(p => Number(p.boom_length_m) === Number(boomM) && (p.variant ?? "") === (variant ?? ""))
    .sort((a, b) => a.radius_m - b.radius_m);
  if (col.length === 0) return null;
  let lo: ChartPoint | null = null;
  let hi: ChartPoint | null = null;
  for (const p of col) {
    if (p.radius_m <= radiusM) lo = p;
    if (p.radius_m >= radiusM && hi === null) hi = p;
  }
  if (!lo || !hi) return null;
  return hi.radius_m;
}

/** Boom lengths we hold a chart for, ascending. */
export function availableBooms(points: ChartPoint[], variant = ""): number[] {
  return Array.from(new Set(
    points.filter(p => (p.variant ?? "") === (variant ?? "")).map(p => Number(p.boom_length_m)),
  )).sort((a, b) => a - b);
}

/**
 * Hook height above the slew centre, from boom geometry. Pythagoras on the
 * boom triangle — enough to sanity-check clearance over the structure, and
 * flagged as indicative because the real figure comes off the crane computer.
 */
export function hookHeightM(boomM: number, radiusM: number, pivotHeightM = 2.1): number | null {
  if (radiusM >= boomM) return null;                 // geometrically impossible
  return Math.sqrt(boomM * boomM - radiusM * radiusM) + pivotHeightM;
}

/** Boom angle from horizontal, for the elevation view. */
export function boomAngleDeg(boomM: number, radiusM: number): number | null {
  if (radiusM >= boomM) return null;
  return Math.acos(radiusM / boomM) * 180 / Math.PI;
}

/**
 * Which boom length should we use? Answers James's "how do I know the boom
 * height" by working it from the two things that actually constrain it:
 *
 *   CAPACITY is read at the GOVERNING radius — the furthest the load is
 *   suspended at (pick or place, whichever is worse). That's the stick-out.
 *   HEIGHT is read at the PLACE radius — the pick is a piece lying in the
 *   laydown; it's landing it on top of what's erected that needs hook height.
 *
 * Returns every charted boom with a verdict, shortest first. Shortest that
 * passes is the answer — a longer boom than you need costs capacity for
 * nothing.
 */
export type BoomOption = {
  boomM: number;
  capacityKg: number | null;
  pctChart: number | null;
  hookHeightM: number | null;
  reachesHeight: boolean;
  hasCapacity: boolean;
  ok: boolean;
};

export function boomOptions(
  points: ChartPoint[],
  opts: {
    grossKg: number; governingRadiusM: number; placeRadiusM: number;
    requiredHeightM: number; variant?: string; headroomM?: number; targetPct?: number;
  },
): BoomOption[] {
  const headroom = opts.headroomM ?? 2;      // slings + clearance over the landing
  const target = opts.targetPct ?? 100;
  return availableBooms(points, opts.variant ?? "").map(boomM => {
    const capacityKg = chartCapacityKg(points, boomM, opts.governingRadiusM, opts.variant ?? "");
    const pctChart = capacityKg && capacityKg > 0 ? (opts.grossKg / capacityKg) * 100 : null;
    const hookH = hookHeightM(boomM, opts.placeRadiusM || opts.governingRadiusM);
    const reachesHeight = hookH != null && hookH >= opts.requiredHeightM + headroom;
    const hasCapacity = pctChart != null && pctChart <= target;
    return {
      boomM, capacityKg, pctChart, hookHeightM: hookH,
      reachesHeight, hasCapacity, ok: reachesHeight && hasCapacity,
    };
  });
}

/** Shortest charted boom that clears both capacity and height, or null. */
export function recommendBoom(points: ChartPoint[], opts: Parameters<typeof boomOptions>[1]): BoomOption | null {
  return boomOptions(points, opts).find(o => o.ok) ?? null;
}

export function computeStage(
  stage: Stage,
  points: ChartPoint[],
  winchCapacityKg?: number | null,
): StageResult {
  const warnings: string[] = [];
  const riggingKg = riggingWeightKg(stage.rigging);
  const fixedKg = (stage.hook_kg || 0) + (stage.rope_kg || 0) + riggingKg;
  const pieceKg = (stage.piece_weight_kg || 0) + (stage.other_allowance_kg || 0);
  let grossKg = pieceKg + fixedKg;
  if (stage.dual_lift) grossKg += (pieceKg + fixedKg) * DUAL_LIFT_FACTOR;

  // The crane has to handle the WORSE of the two — you don't get to average a
  // pick at 8m with a place at 14m.
  const governingRadiusM = Math.max(stage.pick_radius_m || 0, stage.place_radius_m || 0);

  const chartKg = stage.boom_length_m && governingRadiusM
    ? chartCapacityKg(points, stage.boom_length_m, governingRadiusM, stage.variant)
    : null;

  // Which chart row that capacity came off, so the document can show WHY the
  // number is what it is when the radius fell between rows (T-436c).
  const chartRadiusM = stage.boom_length_m && governingRadiusM
    ? chartRadiusUsedM(points, stage.boom_length_m, governingRadiusM, stage.variant)
    : null;

  const pctChart = chartKg && chartKg > 0 ? (grossKg / chartKg) * 100 : null;
  const pctWinch = winchCapacityKg && winchCapacityKg > 0 ? (grossKg / winchCapacityKg) * 100 : null;
  const hookH = stage.boom_length_m && governingRadiusM
    ? hookHeightM(stage.boom_length_m, governingRadiusM) : null;

  let band: StageResult["band"] = "unknown";
  if (pctChart == null) {
    band = "unknown";
    if (stage.boom_length_m && governingRadiusM) {
      warnings.push("Off chart — this radius isn't charted at that boom length. Pick a different configuration or shorten the radius.");
    }
  } else if (pctChart > 100) {
    band = "over";
    warnings.push(`Over capacity — ${pctChart.toFixed(0)}% of the chart. This lift cannot proceed as configured.`);
  } else if (pctChart > 90) {
    band = "caution";
    warnings.push(`${pctChart.toFixed(0)}% of chart — above 90%, get the crane company's study before committing.`);
  } else if (pctChart > 75) {
    band = "caution";
  } else {
    band = "ok";
  }

  // Winch is an independent limit — a lift can pass the chart and fail the rope.
  if (pctWinch != null && pctWinch > 100) {
    warnings.push(`Over main winch capacity (${pctWinch.toFixed(0)}%) — more parts of line needed.`);
    band = "over";
  }
  if (hookH == null && stage.boom_length_m && governingRadiusM) {
    warnings.push("Radius exceeds boom length — check the numbers.");
  }
  // Can the hook actually get above where the piece has to land? Top of the
  // lift = what's already erected + this piece + slings. Cheap check, catches
  // "the chart says yes but the boom is too short" before anyone mobilises.
  const requiredH = (stage.built_height_m || 0) + (stage.piece_length_m || 0);
  if (hookH != null && requiredH > 0 && hookH < requiredH + 2) {
    warnings.push(
      `Hook reaches ${hookH.toFixed(1)} m but the piece tops out at ${requiredH.toFixed(1)} m — ` +
      `needs a longer boom, a shorter radius, or a fly.`,
    );
  }
  if (!stage.piece_weight_kg) warnings.push("Piece weight not entered.");

  return {
    riggingKg, fixedKg, pieceKg, grossKg, governingRadiusM,
    chartKg, chartRadiusM, pctChart, pctWinch, hookHeightM: hookH, band, warnings,
  };
}

/**
 * Ground bearing pressure per outrigger. Borger's formula, verbatim:
 *   outrigger load (t) x 9.81 / pad area (m2)  ->  kPa
 * Pad tare is added because the mat sits on the ground too.
 */
export function bearingPressureKpa(loadT: number, padLengthM: number, padWidthM: number, padTareT = 0): number | null {
  const area = (padLengthM || 0) * (padWidthM || 0);
  if (area <= 0) return null;
  return ((loadT || 0) + (padTareT || 0)) * 9.81 / area;
}

/** Rough soil guidance — indicative only, an engineer signs off marginal ground. */
export function bearingVerdict(kpa: number): { label: string; tone: "ok" | "warning" | "danger" } {
  if (kpa <= 100) return { label: "OK on most compacted ground", tone: "ok" };
  if (kpa <= 200) return { label: "Firm ground or mats required", tone: "warning" };
  if (kpa <= 400) return { label: "Engineered mats — verify ground", tone: "warning" };
  return { label: "High — geotechnical advice required", tone: "danger" };
}

export const fmtKg = (kg: number | null | undefined) =>
  kg == null ? "—" : kg >= 1000 ? `${(kg / 1000).toFixed(2)} t` : `${Math.round(kg)} kg`;
